import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Search,
    Play,
    ChevronRight,
    Briefcase,
    Settings2,
    Users,
    FolderKanban,
    MessagesSquare,
    BrainCircuit,
    Terminal,
    BookOpen,
    Sparkles,
    ArrowUpRight,
} from "lucide-react";

/* ──────────────────────────────────────────────────────────────
 *  Pillars × Features data
 * ──────────────────────────────────────────────────────────── */

const MODULES = [
    {
        id: "sales",
        icon: Briefcase,
        accent: "text-cyan-300",
        accentBg: "bg-cyan-300/10 border-cyan-300/25",
        ring: "from-cyan-400/30 to-cyan-400/0",
        title: "Sales",
        tagline: "Pipeline, deals, quotes & revenue forecasts.",
        overview:
            "ApexBrain.Sales gives revenue teams a single funnel from lead to invoice. Deals, quotes, contacts and forecasts share the same graph as Projects and Finance.",
        features: [
            {
                id: "pipeline",
                title: "Pipeline Management",
                blurb: "Stages, deals, owners and probability — all on one board.",
                bullets: [
                    "Drag-and-drop kanban with stage probabilities",
                    "Custom pipelines per team or motion",
                    "Auto-thread every email and call to the deal",
                ],
                steps: [
                    "Open Sales → Pipelines and click Create Pipeline.",
                    "Define stages and probability weights.",
                    "Assign deal owners and required fields per stage.",
                    "Drag deals between stages to advance them.",
                ],
                cli: "ax sales pipeline create --name 'enterprise' --stages 'discovery,proposal,won'",
            },
            {
                id: "forecast",
                title: "Deal Forecasting",
                blurb: "Weighted, AI-adjusted forecasts updated every hour.",
                bullets: [
                    "Blends pipeline weights with delivery capacity",
                    "Tracks commit vs. best-case vs. closed",
                    "Variance explained by Co-pilot in plain English",
                ],
                steps: [
                    "Open Sales → Forecast — view defaults to current quarter.",
                    "Filter by owner, team, region or pipeline.",
                    "Hover a number to see the Co-pilot's explanation.",
                    "Pin the forecast to a leadership dashboard.",
                ],
                cli: "ax sales forecast --period Q4 --view commit",
            },
            {
                id: "quote",
                title: "Quote Builder",
                blurb: "Generate signed-quote-ready proposals from a deal.",
                bullets: [
                    "Template library with company branding",
                    "Auto-fills from deal, product catalog and account",
                    "E-signature with audit trail",
                ],
                steps: [
                    "Inside a deal, click New Quote.",
                    "Pick a template; line items pull from the catalog.",
                    "Send for signature directly from the quote.",
                    "On signature, deal advances to 'closed-won' automatically.",
                ],
                cli: "ax sales quote new --deal d_3142 --template 'enterprise-msa'",
            },
            {
                id: "scoring",
                title: "Lead Scoring (AI)",
                blurb: "Co-pilot grades leads using firmographic + intent signals.",
                bullets: [
                    "Scores refresh as new activity lands",
                    "Explainable signals — no black-box numbers",
                    "Auto-routes A-grade leads to senior reps",
                ],
                steps: [
                    "Enable AI Lead Scoring in Sales settings.",
                    "Optionally connect your enrichment provider.",
                    "Define routing rules per grade.",
                    "Inspect the why-this-score panel on any lead.",
                ],
                cli: "ax sales scoring enable --rubric 'enterprise-saas'",
            },
            {
                id: "email-sync",
                title: "Email Sync & Sequences",
                blurb: "Two-way sync with Gmail/Outlook + sequenced cadences.",
                bullets: [
                    "Inbox and deal share one timeline",
                    "Sequences pause automatically on reply",
                    "Variants tested with built-in A/B reports",
                ],
                steps: [
                    "Connect your work inbox under Sales → Channels.",
                    "Open a deal and start a sequence.",
                    "Variants and stop conditions are configurable per step.",
                    "Reply detection auto-pauses the cadence.",
                ],
                cli: "ax sales sequence add --deal d_3142 --template 'q4-followup'",
            },
        ],
    },
    {
        id: "operations",
        icon: Settings2,
        accent: "text-electric",
        accentBg: "bg-electric/10 border-electric/25",
        ring: "from-electric/30 to-electric/0",
        title: "Operations",
        tagline: "SOPs, workflows & automations.",
        overview:
            "Operations is the workflow engine of ApexBrain. Build SOPs visually, attach approvals and SLAs, and let the system run the playbook every time.",
        features: [
            {
                id: "workflow-studio",
                title: "Workflow Studio",
                blurb: "Visual builder for any cross-module workflow.",
                bullets: [
                    "Drag-drop steps with conditions and branches",
                    "Templates for onboarding, RFP, incident, etc.",
                    "Publish → workflow runs and audits itself",
                ],
                steps: [
                    "Open Operations → Workflow Studio.",
                    "Pick a template or start blank.",
                    "Drag steps, set owners, attach forms and approval gates.",
                    "Publish — runs are auditable end-to-end.",
                ],
                cli: "ax ops workflow run --name 'employee-onboarding' --input '{\"hire_id\":\"emp_1042\"}'",
            },
            {
                id: "sop",
                title: "SOP Library",
                blurb: "Centralised standard operating procedures with versioning.",
                bullets: [
                    "Versioned SOPs with approval history",
                    "Embed checklists into any workflow step",
                    "Co-pilot drafts new SOPs from past runs",
                ],
                steps: [
                    "Open Operations → SOPs.",
                    "Create a new SOP — markdown or template-based.",
                    "Reference SOP IDs in workflows via #sop-name.",
                    "Co-pilot suggests updates after each run.",
                ],
                cli: "ax ops sop new --name 'post-incident-comms' --owner aanya",
            },
            {
                id: "approvals",
                title: "Approval Engine",
                blurb: "Conditional approvals on any record, with audit log.",
                bullets: [
                    "Single, sequential or parallel approvals",
                    "Escalation rules with SLA timers",
                    "Inline approvals from chat or email",
                ],
                steps: [
                    "Define an approval policy under Operations → Policies.",
                    "Attach to a workflow step or any record type.",
                    "Approvers receive in-product, chat and email notifications.",
                    "Audit log captures every action and timestamp.",
                ],
                cli: "ax ops policy attach --policy 'expense-over-1k' --record-type expense",
            },
            {
                id: "sla",
                title: "SLA Monitoring",
                blurb: "Track time-to-resolve across workflows and tickets.",
                bullets: [
                    "Define SLAs per workflow or record type",
                    "Breach predictions before they happen",
                    "Dashboards group by team, customer or priority",
                ],
                steps: [
                    "Set SLA targets under the workflow's settings.",
                    "Open Operations → SLA Health for the live view.",
                    "Subscribe to alerts on breach risk.",
                    "Co-pilot surfaces patterns and root causes.",
                ],
                cli: "ax ops sla set --workflow 'support-tier1' --target 4h",
            },
            {
                id: "vendor",
                title: "Vendor Management",
                blurb: "Vendors, contracts, renewals and spend in one place.",
                bullets: [
                    "Contract reminders and renewal workflows",
                    "Spend roll-ups by category and team",
                    "Linked from procurement requests automatically",
                ],
                steps: [
                    "Open Operations → Vendors.",
                    "Add a vendor and upload current contract.",
                    "ApexBrain extracts key dates and obligations.",
                    "Renewal workflow triggers 90 days before expiry.",
                ],
                cli: "ax ops vendor add --name 'AWS' --contract ./aws-msa.pdf",
            },
        ],
    },
    {
        id: "hr",
        icon: Users,
        accent: "text-emerald-300",
        accentBg: "bg-emerald-300/10 border-emerald-300/25",
        ring: "from-emerald-400/30 to-emerald-400/0",
        title: "HR",
        tagline: "People, attendance & performance.",
        overview:
            "HR brings every person record into one source of truth — hiring, onboarding, attendance, leave, reviews and payroll inputs — connected to the work they ship.",
        features: [
            {
                id: "people",
                title: "People Records",
                blurb: "Single source of truth for every team member.",
                bullets: [
                    "Profile, contracts, role history and documents",
                    "Compensation history with controlled access",
                    "Direct line to the work they ship in Projects",
                ],
                steps: [
                    "Open HR → People to view the roster.",
                    "Click a profile to see role history and projects.",
                    "Sensitive fields are role-gated by default.",
                    "Bulk import via CSV or HRIS sync.",
                ],
                cli: "ax hr person invite --email 'jane@apexbrain.in' --role 'designer' --team 'product'",
            },
            {
                id: "onboarding",
                title: "Onboarding Workflows",
                blurb: "From signed offer to first sprint in 14 days.",
                bullets: [
                    "Triggered automatically on new hire creation",
                    "Provisions accounts, tools and access by role",
                    "Buddy assignment + starter sprint queued in Projects",
                ],
                steps: [
                    "Configure the onboarding workflow under HR → Workflows.",
                    "Add an offer signed event → workflow auto-runs.",
                    "Personalised first-week plan auto-drafted by the Co-pilot.",
                    "Track time-to-productivity in the HR dashboard.",
                ],
                cli: "ax hr onboarding kickoff --hire emp_1042",
            },
            {
                id: "attendance",
                title: "Attendance & Leave",
                blurb: "Policies, balances, calendars — all explainable.",
                bullets: [
                    "Country-specific policies built in",
                    "Calendar integrates with Projects capacity",
                    "Self-service leave with manager approvals",
                ],
                steps: [
                    "Set policies under HR → Leave Policies.",
                    "Employees request leave from any view.",
                    "Approval flows route to direct managers.",
                    "Approved leave shrinks capacity in Projects automatically.",
                ],
                cli: "ax hr leave apply --type 'casual' --from 2026-03-01 --days 2",
            },
            {
                id: "perf",
                title: "Performance Reviews",
                blurb: "Cycles that aggregate real shipped work, not memory.",
                bullets: [
                    "Quarterly or annual cycles, templated",
                    "Auto-pulls evidence from Projects and Sales",
                    "Calibration view across managers",
                ],
                steps: [
                    "Open HR → Performance and start a cycle.",
                    "Self-reviews open first, then manager reviews.",
                    "Calibration meeting view rolls up the cohort.",
                    "Outcomes flow into comp planning automatically.",
                ],
                cli: "ax hr cycle start --period 'q4-2026' --template 'standard'",
            },
            {
                id: "payroll",
                title: "Payroll Inputs",
                blurb: "Approved attendance, leave and incentives, one click.",
                bullets: [
                    "Country-specific export formats",
                    "Locks the period after export",
                    "Integrates with Gusto, Rippling, Deel",
                ],
                steps: [
                    "Open HR → Payroll.",
                    "Review monthly summary and approve.",
                    "Export to your payroll provider.",
                    "The period is locked from further edits.",
                ],
                cli: "ax hr payroll export --period 2026-02 --provider gusto",
            },
        ],
    },
    {
        id: "projects",
        icon: FolderKanban,
        accent: "text-amber-glow",
        accentBg: "bg-amber-300/10 border-amber-300/25",
        ring: "from-amber-400/30 to-amber-400/0",
        title: "Projects",
        tagline: "Tasks, sprints, roadmaps tied to revenue.",
        overview:
            "Projects ties every sprint and task back to the deal that paid for it. Capacity, margin and timeline live next to the work.",
        features: [
            {
                id: "sprints",
                title: "Sprints & Tasks",
                blurb: "Full Scrum cycle with planning, standups and retros.",
                bullets: [
                    "Plan, kanban and timeline views",
                    "Capacity-aware assignment",
                    "Standup digests auto-generated daily",
                ],
                steps: [
                    "Create a project and pick a workflow (Scrum, Kanban, Hybrid).",
                    "Plan a sprint — drag tasks from the backlog.",
                    "Assign owners; capacity warnings appear inline.",
                    "Run standups inside the project's chat panel.",
                ],
                cli: "ax projects task add --project 'website-redesign' --title 'Hero polish' --assignee aanya",
            },
            {
                id: "roadmap",
                title: "Roadmap Timeline",
                blurb: "Quarterly view across teams and initiatives.",
                bullets: [
                    "Group by team, OKR or strategic theme",
                    "Drag bars to re-plan; dependencies update",
                    "Snapshot the roadmap weekly for change history",
                ],
                steps: [
                    "Open Projects → Roadmap.",
                    "Drag tasks into quarters and lanes.",
                    "Click any bar to inspect dependencies.",
                    "Pin the view to share with leadership.",
                ],
                cli: "ax projects roadmap snapshot --label '2026-q4-plan'",
            },
            {
                id: "capacity",
                title: "Capacity Planning",
                blurb: "Plan against real availability, including leave.",
                bullets: [
                    "Pulls hours from HR.Attendance",
                    "Detects overcommit before sprint starts",
                    "Suggests rebalancing across team",
                ],
                steps: [
                    "Open the project's Capacity view.",
                    "Inspect per-person availability for the period.",
                    "Rebalance with the Co-pilot's suggestions.",
                    "Confirm the plan — sprint locks the assignments.",
                ],
                cli: "ax projects capacity rebalance --sprint s_2026_05",
            },
            {
                id: "burndown",
                title: "Sprint Burn-down",
                blurb: "Real-time progress and risk indicators.",
                bullets: [
                    "Ideal vs actual chart, daily",
                    "Blocker surfaced with owner notified",
                    "End-of-sprint summary drafted automatically",
                ],
                steps: [
                    "Each sprint includes a Burn-down tab.",
                    "Hover the line to see daily deltas.",
                    "Blockers surface as orange markers.",
                    "End-of-sprint summary uses real evidence.",
                ],
                cli: "ax projects sprint summary --sprint s_2026_05",
            },
            {
                id: "margin",
                title: "Margin Tracking",
                blurb: "Live margin per engagement, by hour and cost.",
                bullets: [
                    "Cost rates pulled from HR roles",
                    "Bill rate from the linked Sales deal",
                    "Margin updates as work is logged",
                ],
                steps: [
                    "Link a project to its Sales deal.",
                    "Hours logged on tasks aggregate as cost.",
                    "Open the Margin tab for the live view.",
                    "Subscribe to alerts on margin slippage.",
                ],
                cli: "ax projects margin show --project p_acme_q4",
            },
        ],
    },
    {
        id: "communication",
        icon: MessagesSquare,
        accent: "text-violet-300",
        accentBg: "bg-violet-300/10 border-violet-300/25",
        ring: "from-violet-400/30 to-violet-400/0",
        title: "Communication",
        tagline: "Chat, docs & decisions threaded to work.",
        overview:
            "Communication keeps every conversation attached to the record it's about — no more lost decisions in DMs.",
        features: [
            {
                id: "chat",
                title: "Threaded Chat",
                blurb: "Conversations attached to deals, projects and people.",
                bullets: [
                    "One panel away from any record",
                    "Participants auto-suggested from owners",
                    "Convert messages to tasks inline",
                ],
                steps: [
                    "Open any record → click the Chat icon.",
                    "Start a thread; add participants as needed.",
                    "Right-click any message to convert to task or decision.",
                    "Notifications respect quiet hours per user.",
                ],
                cli: "ax chat thread open --record 'deal:acme-q4'",
            },
            {
                id: "decision",
                title: "Decision Log",
                blurb: "Pinned decisions become first-class citizens.",
                bullets: [
                    "Pin from any chat, doc or meeting",
                    "Searchable across the company",
                    "Linked to the records they affect",
                ],
                steps: [
                    "Pin a message as a decision.",
                    "Add context, owner and effective date.",
                    "It surfaces on the record's timeline forever.",
                    "Search by tag or impact.",
                ],
                cli: "ax decision pin --message msg_8821 --owner vikram",
            },
            {
                id: "docs",
                title: "Live Docs",
                blurb: "Collaborative documents with embedded records.",
                bullets: [
                    "Real-time editing with presence",
                    "Embed live tables, charts and records",
                    "Versioned with restore on any change",
                ],
                steps: [
                    "Create a doc from Communication → Docs.",
                    "Embed a live table with /table apexbrain://...",
                    "Invite collaborators or share read-only.",
                    "Restore any earlier version from history.",
                ],
                cli: "ax docs new --title 'Q4 Plan' --in 'space:leadership'",
            },
            {
                id: "search",
                title: "Universal Search",
                blurb: "One bar across chat, docs, records and decisions.",
                bullets: [
                    "Semantic + keyword hybrid",
                    "Permission-aware results",
                    "Co-pilot answers in-line when relevant",
                ],
                steps: [
                    "Press ⌘K from anywhere.",
                    "Type a query — results group by type.",
                    "Use modifiers like 'in:projects' or 'owner:me'.",
                    "Co-pilot summarises when results agree.",
                ],
                cli: "ax search 'q4 pipeline forecast' --scope sales,projects",
            },
            {
                id: "calls",
                title: "Call Snippets",
                blurb: "Recordings, transcripts and highlights on the record.",
                bullets: [
                    "Connect Zoom, Meet or native calls",
                    "Auto-transcribed with speaker labels",
                    "Co-pilot drafts follow-up tasks",
                ],
                steps: [
                    "Connect your meeting tool under Communication → Channels.",
                    "Calls related to a record auto-attach.",
                    "Transcript is searchable like any doc.",
                    "Approve drafted follow-ups in one click.",
                ],
                cli: "ax calls attach --record 'deal:acme-q4' --recording r_55",
            },
        ],
    },
    {
        id: "intelligence",
        icon: BrainCircuit,
        accent: "text-rose-300",
        accentBg: "bg-rose-300/10 border-rose-300/25",
        ring: "from-rose-400/30 to-rose-400/0",
        title: "Intelligence",
        tagline: "The AI co-pilot that reasons across modules.",
        overview:
            "Intelligence reads the whole company graph. Ask anything — pipeline health, attrition risk — and it answers with citations to the underlying records.",
        features: [
            {
                id: "qa",
                title: "Cross-module Q&A",
                blurb: "Ask in plain English, get answers grounded in your data.",
                bullets: [
                    "Scope to a module or run cross-company",
                    "Answers are cached and re-runnable",
                    "Promote useful queries to dashboards",
                ],
                steps: [
                    "Press ⌘K and switch to Ask Co-pilot.",
                    "Type a question, optionally add scopes.",
                    "Inspect citations or run a follow-up.",
                    "Pin the answer to a dashboard.",
                ],
                cli: "ax ai ask --q 'Why did Q4 pipeline slip vs Q3?' --scope 'sales,projects'",
            },
            {
                id: "citations",
                title: "Citations & Audit",
                blurb: "Every answer links to the records that produced it.",
                bullets: [
                    "No hallucinations — answers cite real records",
                    "Audit log on every Co-pilot reasoning chain",
                    "Disable LLM steps for regulated workloads",
                ],
                steps: [
                    "Click any Co-pilot answer to expand its citations.",
                    "Each citation links to the source record.",
                    "Compliance officers can replay the reasoning chain.",
                    "Enterprise tier supports BYO-model and on-prem.",
                ],
                cli: "ax ai audit --answer a_22193",
            },
            {
                id: "briefings",
                title: "Auto Briefings",
                blurb: "Personalised daily / weekly briefings per role.",
                bullets: [
                    "Built from real shipped work, not vibes",
                    "Per-role templates (Founder, COO, Sales Lead)",
                    "Delivered to inbox, Slack or in-product",
                ],
                steps: [
                    "Open Intelligence → Briefings.",
                    "Pick a template or build your own.",
                    "Schedule per-day, per-week or on a trigger.",
                    "Receive in Slack, email or in-product.",
                ],
                cli: "ax ai briefing subscribe --template 'founder-monday' --to slack",
            },
            {
                id: "anomaly",
                title: "Anomaly Detection",
                blurb: "Quiet alerts when numbers move outside their normal band.",
                bullets: [
                    "Self-tuning baselines per metric",
                    "Explains the change inline",
                    "Subscribe per metric, per team",
                ],
                steps: [
                    "Open any metric in Intelligence.",
                    "Click Watch for anomalies.",
                    "Receive an explainable alert when triggered.",
                    "Drill from alert → record in two clicks.",
                ],
                cli: "ax ai anomaly watch --metric 'sales.forecast.commit'",
            },
            {
                id: "suggestions",
                title: "Workflow Suggestions",
                blurb: "Co-pilot proposes automations from observed patterns.",
                bullets: [
                    "Spots repeated manual sequences",
                    "Proposes a workflow you can accept in one click",
                    "Edit before publishing — never auto-runs",
                ],
                steps: [
                    "Open Intelligence → Suggestions.",
                    "Review the proposed automations with rationales.",
                    "Edit, accept or dismiss each suggestion.",
                    "Accepted suggestions land in Workflow Studio.",
                ],
                cli: "ax ai suggest list --since 7d",
            },
        ],
    },
];

/* ──────────────────────────────────────────────────────────────
 *  Page
 * ──────────────────────────────────────────────────────────── */

export default function DocsPage() {
    const [activeId, setActiveId] = useState(MODULES[0].id);
    const [featureId, setFeatureId] = useState(MODULES[0].features[0].id);
    const [q, setQ] = useState("");

    const active = MODULES.find((m) => m.id === activeId) || MODULES[0];
    const feature =
        active.features.find((f) => f.id === featureId) || active.features[0];

    const selectPillar = (id) => {
        setActiveId(id);
        const next = MODULES.find((m) => m.id === id) || MODULES[0];
        setFeatureId(next.features[0].id);
    };

    const filtered = useMemo(() => {
        if (!q.trim()) return MODULES;
        const s = q.toLowerCase();
        return MODULES.filter(
            (m) =>
                m.title.toLowerCase().includes(s) ||
                m.tagline.toLowerCase().includes(s) ||
                m.overview.toLowerCase().includes(s) ||
                m.features.some(
                    (f) =>
                        f.title.toLowerCase().includes(s) ||
                        f.blurb.toLowerCase().includes(s),
                ),
        );
    }, [q]);

    return (
        <div className="pt-28 lg:pt-32" data-testid="docs-page">
            {/* Hero */}
            <section className="relative overflow-hidden py-12 lg:py-16">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                        <div className="lg:col-span-7">
                            <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                                // documentation · v2.4 · {MODULES.reduce(
                                    (n, m) => n + m.features.length,
                                    0,
                                )}{" "}
                                features
                            </p>
                            <h1
                                className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                                data-testid="docs-title"
                            >
                                Build with ApexBrain.
                                <br />
                                <span className="gradient-text">
                                    Pillar by pillar. Feature by feature.
                                </span>
                            </h1>
                        </div>
                        <p className="lg:col-span-5 text-base sm:text-lg text-zinc-400 leading-relaxed font-light">
                            Each pillar ships with a feature catalog —
                            explainer video, capabilities, usage steps and CLI
                            for every one.
                        </p>
                    </div>

                    {/* Search */}
                    <div className="mt-10 max-w-xl relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search docs…  e.g. ‘onboarding workflow’"
                            data-testid="docs-search"
                            className="w-full pl-11 pr-4 py-3 rounded-full bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white placeholder:text-zinc-600 font-code transition"
                        />
                    </div>
                </div>
            </section>

            {/* Main */}
            <section className="relative pb-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                    {/* Sidebar — pillars */}
                    <aside className="lg:col-span-3">
                        <div className="lg:sticky lg:top-28 space-y-1">
                            <p className="font-code text-[10px] uppercase tracking-[0.25em] text-zinc-600 px-3 mb-2">
                                // pillars
                            </p>
                            {filtered.map((m) => {
                                const isActive = m.id === activeId;
                                return (
                                    <button
                                        key={m.id}
                                        onClick={() => selectPillar(m.id)}
                                        data-testid={`docs-tab-${m.id}`}
                                        className={`w-full group flex items-center gap-3 text-left px-3 py-2.5 rounded-lg border transition ${
                                            isActive
                                                ? "bg-white/[0.04] border-white/15"
                                                : "bg-transparent border-transparent hover:bg-white/[0.02] hover:border-white/[0.06]"
                                        }`}
                                    >
                                        <span
                                            className={`inline-flex items-center justify-center w-7 h-7 rounded-md border ${
                                                isActive
                                                    ? m.accentBg
                                                    : "bg-white/[0.03] border-white/[0.06]"
                                            }`}
                                        >
                                            <m.icon
                                                className={`w-3.5 h-3.5 ${isActive ? m.accent : "text-zinc-500"}`}
                                            />
                                        </span>
                                        <span
                                            className={`flex-1 text-sm ${isActive ? "text-white font-semibold" : "text-zinc-400 group-hover:text-zinc-200"}`}
                                        >
                                            {m.title}
                                            <span className="ml-2 text-[10px] font-code text-zinc-600">
                                                {m.features.length}
                                            </span>
                                        </span>
                                        <ChevronRight
                                            className={`w-3.5 h-3.5 transition ${isActive ? m.accent : "text-zinc-700"}`}
                                        />
                                    </button>
                                );
                            })}
                            {filtered.length === 0 && (
                                <p className="text-xs text-zinc-500 px-3 py-2">
                                    No modules match “{q}”.
                                </p>
                            )}
                        </div>
                    </aside>

                    {/* Right pane */}
                    <div className="lg:col-span-9 min-w-0 space-y-6">
                        {/* Pillar overview header */}
                        <AnimatePresence mode="wait">
                            <motion.header
                                key={active.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                    transition: {
                                        duration: 0.3,
                                        ease: [0.16, 1, 0.3, 1],
                                    },
                                }}
                                exit={{
                                    opacity: 0,
                                    y: -6,
                                    transition: {
                                        duration: 0.15,
                                        ease: [0.4, 0, 1, 1],
                                    },
                                }}
                                className="relative rounded-2xl border border-white/[0.08] bg-obsidian-200/70 backdrop-blur-sm p-7 sm:p-9 overflow-hidden"
                                data-testid={`docs-content-${active.id}`}
                            >
                                <div
                                    className={`absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-br ${active.ring} blur-3xl pointer-events-none`}
                                />
                                <div className="relative flex items-center gap-3 mb-4">
                                    <span
                                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 ${active.accentBg}`}
                                    >
                                        <active.icon
                                            className={`w-3.5 h-3.5 ${active.accent}`}
                                        />
                                        <span
                                            className={`text-[10px] font-code uppercase tracking-[0.2em] font-semibold ${active.accent}`}
                                        >
                                            ApexBrain.{active.title}
                                        </span>
                                    </span>
                                    <span className="font-code text-[11px] text-zinc-500">
                                        v2.4 · {active.features.length} features
                                    </span>
                                </div>
                                <h2 className="relative font-outfit text-2xl sm:text-3xl lg:text-4xl font-semibold text-white leading-[1.1]">
                                    {active.tagline}
                                </h2>
                                <p className="relative mt-4 max-w-3xl text-zinc-400 leading-relaxed font-light">
                                    {active.overview}
                                </p>
                            </motion.header>
                        </AnimatePresence>

                        {/* Feature catalog — pills + cards */}
                        <div className="relative rounded-2xl border border-white/[0.08] bg-obsidian-200/40 backdrop-blur-sm p-5 sm:p-6">
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-3.5 h-3.5 text-electric" />
                                    <p className="font-code text-[11px] uppercase tracking-[0.2em] text-zinc-400">
                                        // {active.title.toLowerCase()} ·
                                        feature catalog
                                    </p>
                                </div>
                                <span className="font-code text-[11px] text-zinc-600">
                                    {active.features.length} features ·
                                    click to expand
                                </span>
                            </div>

                            <div
                                className="flex flex-wrap gap-2 mb-6"
                                data-testid="docs-feature-tabs"
                            >
                                {active.features.map((f) => {
                                    const isActive = f.id === feature.id;
                                    return (
                                        <button
                                            key={f.id}
                                            onClick={() => setFeatureId(f.id)}
                                            data-testid={`docs-feature-tab-${active.id}-${f.id}`}
                                            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
                                                isActive
                                                    ? `${active.accentBg} text-white`
                                                    : "bg-white/[0.02] border-white/[0.06] text-zinc-400 hover:bg-white/[0.05]"
                                            }`}
                                        >
                                            <span
                                                className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400 animate-pulse-glow" : "bg-zinc-600"}`}
                                            />
                                            {f.title}
                                        </button>
                                    );
                                })}
                            </div>

                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={`${active.id}-${feature.id}`}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{
                                        opacity: 1,
                                        y: 0,
                                        transition: {
                                            duration: 0.3,
                                            ease: [0.16, 1, 0.3, 1],
                                        },
                                    }}
                                    exit={{
                                        opacity: 0,
                                        y: -6,
                                        transition: {
                                            duration: 0.15,
                                            ease: [0.4, 0, 1, 1],
                                        },
                                    }}
                                    className="space-y-7"
                                    data-testid={`docs-feature-content-${active.id}-${feature.id}`}
                                >
                                    {/* Headline */}
                                    <div>
                                        <h3 className="font-outfit text-xl sm:text-2xl font-semibold text-white">
                                            {feature.title}
                                        </h3>
                                        <p
                                            className={`mt-1.5 text-sm sm:text-base ${active.accent}`}
                                        >
                                            {feature.blurb}
                                        </p>
                                    </div>

                                    {/* Capability bullets */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        {feature.bullets.map((b, i) => (
                                            <div
                                                key={b}
                                                className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4"
                                            >
                                                <div className="font-code text-[10px] uppercase tracking-[0.18em] text-zinc-600 mb-1.5">
                                                    capability ·{" "}
                                                    {String(i + 1).padStart(2, "0")}
                                                </div>
                                                <div className="text-sm text-zinc-200 leading-relaxed">
                                                    {b}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Video placeholder */}
                                    <div>
                                        <p className="font-code text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                                            // explainer · 90 sec
                                        </p>
                                        <div
                                            className="group relative aspect-video rounded-2xl border border-white/[0.08] overflow-hidden bg-gradient-to-br from-obsidian-100 to-obsidian-300 cursor-pointer"
                                            data-testid={`docs-video-${active.id}-${feature.id}`}
                                        >
                                            <div
                                                className={`absolute inset-0 bg-gradient-to-br ${active.ring} opacity-40`}
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="flex items-center gap-4">
                                                    <span
                                                        className={`relative inline-flex items-center justify-center w-14 h-14 rounded-full border ${active.accentBg} backdrop-blur-md`}
                                                    >
                                                        <Play
                                                            className={`w-5 h-5 ml-0.5 ${active.accent}`}
                                                        />
                                                        <span className="absolute inset-0 rounded-full animate-ping bg-white/5" />
                                                    </span>
                                                    <div>
                                                        <div className="font-outfit text-base text-white font-semibold">
                                                            {feature.title} ·
                                                            tour
                                                        </div>
                                                        <div className="font-code text-[10px] text-zinc-500 mt-0.5">
                                                            apexbrain.in/docs/
                                                            {active.id}/
                                                            {feature.id}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 font-code text-[10px] text-zinc-500 bg-black/40 border border-white/[0.06] rounded px-2 py-1">
                                                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse-glow" />
                                                preview · 01:24
                                            </div>
                                        </div>
                                    </div>

                                    {/* Usage steps */}
                                    <div>
                                        <p className="font-code text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                                            // usage · {feature.steps.length}{" "}
                                            steps
                                        </p>
                                        <ol className="space-y-3">
                                            {feature.steps.map((s, i) => (
                                                <li
                                                    key={s}
                                                    className="flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4"
                                                    data-testid={`docs-step-${active.id}-${feature.id}-${i}`}
                                                >
                                                    <span
                                                        className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border ${active.accentBg} font-code text-[11px] font-semibold ${active.accent}`}
                                                    >
                                                        {String(i + 1).padStart(2, "0")}
                                                    </span>
                                                    <p className="text-sm sm:text-base text-zinc-200 leading-relaxed">
                                                        {s}
                                                    </p>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>

                                    {/* CLI snippet */}
                                    <div>
                                        <p className="font-code text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3 inline-flex items-center gap-1.5">
                                            <Terminal className="w-3 h-3" />
                                            // cli · try it
                                        </p>
                                        <pre className="rounded-xl border border-white/[0.08] bg-black/50 p-5 overflow-x-auto font-code text-[12.5px] leading-relaxed text-zinc-200">
                                            <span className="text-zinc-600 select-none">
                                                ${" "}
                                            </span>
                                            <span>{feature.cli}</span>
                                        </pre>
                                    </div>

                                    {/* Footer */}
                                    <div className="pt-3 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-code text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                                                // jump to
                                            </span>
                                            {active.features
                                                .filter(
                                                    (f) =>
                                                        f.id !== feature.id,
                                                )
                                                .slice(0, 3)
                                                .map((f) => (
                                                    <button
                                                        key={f.id}
                                                        onClick={() =>
                                                            setFeatureId(f.id)
                                                        }
                                                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] ${active.accentBg} hover:brightness-125 transition`}
                                                    >
                                                        <BookOpen
                                                            className={`w-3 h-3 ${active.accent}`}
                                                        />
                                                        <span
                                                            className={active.accent}
                                                        >
                                                            {f.title}
                                                        </span>
                                                    </button>
                                                ))}
                                        </div>
                                        <a
                                            href="/use-cases"
                                            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition"
                                        >
                                            See this feature in a real flow
                                            <ArrowUpRight className="w-3 h-3" />
                                        </a>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
